import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import type { ShowToast, FetchData } from '../types/appShared';

// ══════════════════════════════════════════════
// usePhotosController — نفس فكرة باقي الـ controllers. منطق الرفع الجماعي
// (bulk upload: handleBulkUpload) بالفعل موجود جوّا PhotosView.jsx نفسها
// (بتستخدم compressImage/refreshPhotos اللي بتتبعت ليها كـ props)، فمحتاجين
// بس نقل الـ state ودالتين refreshPhotos/deletePhoto هنا.
// photosData/setPhotosData و modalPhoto/setModalPhoto و compressImage اتسابوا
// في App.jsx: الأول بيتحمّل من fetchData المركزية، والتاني مُستخدم في مودال
// الـ lightbox بتاع App.jsx نفسه، والتالت (compressImage) مُستخدمة كمان من
// Daily Activities (مش خاصة بـ Photos بس).
// ══════════════════════════════════════════════
export type UploadStatus = 'pending' | 'uploading' | 'done' | 'error';

interface UsePhotosControllerParams {
    currentProjectId: string | null;
    showToast: ShowToast;
    fetchData: FetchData;
    setPhotosData: (photos: any[]) => void;
}

export function usePhotosController({ currentProjectId, showToast, fetchData, setPhotosData }: UsePhotosControllerParams) {
    const [photoSearch, setPhotoSearch] = useState('');
    const [photoSort, setPhotoSort] = useState('newest');
    const [photoView, setPhotoView] = useState('grid'); // grid | masonry | list
    const [selectedSystemFilter, setSelectedSystemFilter] = useState('all');
    const [photosPage, setPhotosPage] = useState(1);

    useEffect(() => { setPhotosPage(1); }, [photoSearch, selectedSystemFilter]);

    const [multiFiles, setMultiFiles] = useState<File[]>([]);
    const [multiSys, setMultiSys] = useState('');
    const [multiDesc, setMultiDesc] = useState('');
    const [multiProgress, setMultiProgress] = useState<Record<string, UploadStatus>>({});
    const [isBulkUploading, setIsBulkUploading] = useState(false);

    const refreshPhotos = async () => {
        if (!currentProjectId) return;
        const { data: photos } = await supabase
            .from('photos')
            .select('*')
            .eq('project_id', currentProjectId)
            .order('uploaded_at', { ascending: false });
        if (photos) setPhotosData(photos);
    };

    const deletePhoto = async (photoId: string | number, fileUrl?: string | null) => {
        try {
            let filePath = null;

            if (fileUrl) {
                const match = fileUrl.split('/public/photos/');
                if (match.length > 1) {
                    filePath = decodeURIComponent(match[1]);
                }
            }

            const { error: dbError } = await supabase
                .from('photos')
                .delete()
                .eq('id', photoId)
                .eq('project_id', currentProjectId);

            if (dbError) throw dbError;

            if (filePath) {
                const { error: storageError } = await supabase.storage
                    .from('photos')
                    .remove([filePath]);

                if (storageError) {
                    console.warn('Storage delete warning:', storageError);
                }
            }

            showToast('✅ Photo deleted successfully!');
            fetchData();

        } catch (err: any) {
            console.error('Delete failed:', err);
            showToast('❌ Failed to delete photo. Please try again.');
        }
    };

    return {
        photoSearch, setPhotoSearch, photoSort, setPhotoSort, photoView, setPhotoView,
        selectedSystemFilter, setSelectedSystemFilter, photosPage, setPhotosPage,
        multiFiles, setMultiFiles, multiSys, setMultiSys, multiDesc, setMultiDesc,
        multiProgress, setMultiProgress, isBulkUploading, setIsBulkUploading,
        refreshPhotos, deletePhoto,
    };
}

export default usePhotosController;
